// ./src/components/features/Users/UsersTable/UsersTable.tsx
import React, { useMemo } from "react";
import { Group, Text, Badge } from "@mantine/core";

import { User, UserAccount } from "@/utils/api";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";
import { buildFullName } from "@/utils";
import { useApp } from "@/contexts";
import { USER_ROLES } from "@/config/userRoles";

export interface UsersTableProps {
  /** Array of users to display */
  users: User[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditUser?: (user: User) => void;

  /** Callback when remove action is clicked */
  onRemoveUser?: (user: User) => void;

  /** ID of user currently being removed */
  removingUserId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditUser,
  onRemoveUser,
  removingUserId,
  size = "md",
}) => {
  const { user } = useApp();

  // Define table columns
  const columns: AppTableColumn<User>[] = useMemo(
    () => [
      {
        key: "username",
        label: "Username",
        sortable: true,
        width: 200,
        minWidth: 200,
        hideOnMobile: true,
        render: (value) => <Text c="dimmed">{value || "—"}</Text>,
      },
      {
        key: "name",
        label: "Full Name",
        sortable: true,
        flex: true,
        minWidth: 300,
        render: (_, record) => (
          <Text fw={500}>{buildFullName(record) || "—"}</Text>
        ),
      },
      {
        key: "role",
        label: "Role",
        sortable: true,
        width: 200,
        minWidth: 200,
        render: (_, record) => (
          <Text fw={500}>
            {record.userAccounts
              ?.reduce((acc: any[], ua: UserAccount) => {
                acc.push(ua.userRole?.roleName ?? "");
                return acc;
              }, [])
              .join(", ")}
          </Text>
        ),
      },
      {
        key: "email",
        label: "Email",
        sortable: true,

        width: 300,
        minWidth: 200,
        render: (value, record) => (
          <Group gap="xs">
            <Text>{value || "—"}</Text>
            {record.emailConfirmation && (
              <Badge size="xs" color="blue" variant="light">
                Verified
              </Badge>
            )}
          </Group>
        ),
      },

      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 100,
        align: "left",
        render: (value) => <StatusBadge status={value} />,
      },
      {
        key: "actions",
        label: "",
        width: 120,
        minWidth: 120,
        align: "right",
        render: (_, record) => {
          const hasOwnerRole = record.userAccounts?.some(
            (ua) => ua?.userRoleId === USER_ROLES.OWNER
          );
          return (
            <Group gap="sm" justify="flex-end">
              <RecordActions
                context={record}
                onEditClick={onEditUser}
                onRemoveClick={onRemoveUser}
                removeInProgress={removingUserId === record.id}
                editTooltip="Edit user"
                removeTooltip="Delete user"
                removeDisabled={record.id === user?.id || hasOwnerRole}
                editDisabled={record.id === user?.id || hasOwnerRole}
              />
            </Group>
          );
        },
      },
    ],
    [onEditUser, onRemoveUser, removingUserId]
  );

  return (
    <AppTable
      data={users}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(user) => user?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
